import {
  getGoogleLoginHintForCurrentOrigin,
  hasGoogleOauthProvider,
  isOriginSupportedForOauth,
  projectRuntimeAmplifyOutputsEnv,
  type RuntimeAmplifyOutputsEnv,
} from "../auth/oauthRuntimeConfig";
import {
  isManagedGoogleAuthOrigin,
  resolveManagedGoogleAuthProfile,
} from "../../config/googleAuthPolicy";

const TRUE_VALUES = new Set(["1", "true", "yes", "on"]);
const FALSE_VALUES = new Set(["0", "false", "no", "off"]);

export type PublicAuthEnv = {
  DEV?: boolean;
  VITE_AUTH_GOOGLE_ENABLED?: string;
  VITE_AUTH_GOOGLE_LOGIN_HINT?: string;
  VITE_AUTH_LOCAL_PERSONA_SIGNIN_ENABLED?: string;
  VITE_AUTH_LOCAL_PERSONA_SKIP_PASSWORD?: string;
} & RuntimeAmplifyOutputsEnv;

export const projectPublicAuthEnv = (
  env: Record<string, string | boolean | undefined> = {},
): PublicAuthEnv => ({
  ...projectRuntimeAmplifyOutputsEnv(env),
  DEV: typeof env.DEV === "boolean" ? env.DEV : undefined,
  VITE_AUTH_GOOGLE_ENABLED:
    typeof env.VITE_AUTH_GOOGLE_ENABLED === "string"
      ? env.VITE_AUTH_GOOGLE_ENABLED
      : undefined,
  VITE_AUTH_GOOGLE_LOGIN_HINT:
    typeof env.VITE_AUTH_GOOGLE_LOGIN_HINT === "string"
      ? env.VITE_AUTH_GOOGLE_LOGIN_HINT
      : undefined,
  VITE_AUTH_LOCAL_PERSONA_SIGNIN_ENABLED:
    typeof env.VITE_AUTH_LOCAL_PERSONA_SIGNIN_ENABLED === "string"
      ? env.VITE_AUTH_LOCAL_PERSONA_SIGNIN_ENABLED
      : undefined,
  VITE_AUTH_LOCAL_PERSONA_SKIP_PASSWORD:
    typeof env.VITE_AUTH_LOCAL_PERSONA_SKIP_PASSWORD === "string"
      ? env.VITE_AUTH_LOCAL_PERSONA_SKIP_PASSWORD
      : undefined,
});

const DEFAULT_PUBLIC_AUTH_ENV: PublicAuthEnv = projectPublicAuthEnv({
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD,
  VITE_YSTNJ_AMPLIFY_OUTPUTS_PROFILE:
    import.meta.env.VITE_YSTNJ_AMPLIFY_OUTPUTS_PROFILE,
  VITE_AUTH_GOOGLE_ENABLED: import.meta.env.VITE_AUTH_GOOGLE_ENABLED,
  VITE_AUTH_GOOGLE_LOGIN_HINT: import.meta.env.VITE_AUTH_GOOGLE_LOGIN_HINT,
  VITE_AUTH_LOCAL_PERSONA_SIGNIN_ENABLED:
    import.meta.env.VITE_AUTH_LOCAL_PERSONA_SIGNIN_ENABLED,
  VITE_AUTH_LOCAL_PERSONA_SKIP_PASSWORD:
    import.meta.env.VITE_AUTH_LOCAL_PERSONA_SKIP_PASSWORD,
});

const parseBooleanEnv = (value?: string): boolean | null => {
  if (!value) {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (TRUE_VALUES.has(normalized)) {
    return true;
  }
  if (FALSE_VALUES.has(normalized)) {
    return false;
  }
  return null;
};

const getCurrentOrigin = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.location.origin;
};

const isDevEnv = (env: PublicAuthEnv): boolean => env.DEV === true;

export const isNonProductionAuthSurface = ({
  env = DEFAULT_PUBLIC_AUTH_ENV,
  origin = getCurrentOrigin(),
}: {
  env?: PublicAuthEnv;
  origin?: string | null;
} = {}): boolean => isDevEnv(env) || isManagedGoogleAuthOrigin(origin);

export const isLocalPersonaAuthSurface = ({
  env = DEFAULT_PUBLIC_AUTH_ENV,
  origin = getCurrentOrigin(),
}: {
  env?: PublicAuthEnv;
  origin?: string | null;
} = {}): boolean =>
  isDevEnv(env) || resolveManagedGoogleAuthProfile(origin) === "local";

export const isProductionAuthSurface = (origin: string | null | undefined): boolean =>
  resolveManagedGoogleAuthProfile(origin) === "production";

export type PostAuthPersonaSelectionChoice = "staff" | "administrator" | "parent";
export type PostAuthPersonaSelectionMode = "disabled" | "open" | "granted-groups";

const normalizeAuthGroupToken = (value: unknown): string =>
  String(value ?? "").trim().toLowerCase();

export const resolveGrantedPostAuthPersonaChoices = (
  groups: readonly unknown[] | null | undefined,
): PostAuthPersonaSelectionChoice[] => {
  const normalizedGroups = new Set((groups ?? []).map(normalizeAuthGroupToken));
  const choices: PostAuthPersonaSelectionChoice[] = [];
  if (
    normalizedGroups.has("staff")
    || normalizedGroups.has("editor")
    || normalizedGroups.has("nurse")
  ) {
    choices.push("staff");
  }
  if (
    normalizedGroups.has("admin")
    || normalizedGroups.has("administrator")
    || normalizedGroups.has("owner")
    || normalizedGroups.has("super-administrator")
  ) {
    choices.push("administrator");
  }
  if (normalizedGroups.has("parent")) {
    choices.push("parent");
  }
  return choices;
};

export const resolvePostAuthPersonaSelectionMode = ({
  isGoogleProvider,
  isIntellioUser,
  groups,
  origin = getCurrentOrigin(),
}: {
  isGoogleProvider?: boolean | null;
  isIntellioUser?: boolean | null;
  groups?: readonly unknown[] | null;
  origin?: string | null;
}): PostAuthPersonaSelectionMode => {
  if (isGoogleProvider !== true) {
    return "disabled";
  }
  if (!isProductionAuthSurface(origin)) {
    return "open";
  }
  if (isIntellioUser === true) {
    return "open";
  }
  return resolveGrantedPostAuthPersonaChoices(groups).length > 1
    ? "granted-groups"
    : "disabled";
};

export const resolvePostAuthPersonaSelectionAllowed = ({
  isGoogleProvider,
  isIntellioUser,
  groups,
  origin = getCurrentOrigin(),
}: {
  isGoogleProvider?: boolean | null;
  isIntellioUser?: boolean | null;
  groups?: readonly unknown[] | null;
  origin?: string | null;
}): boolean =>
  resolvePostAuthPersonaSelectionMode({
    isGoogleProvider,
    isIntellioUser,
    groups,
    origin,
  }) !== "disabled";

export const resolveGoogleSocialEnabled = ({
  env = DEFAULT_PUBLIC_AUTH_ENV,
  origin = getCurrentOrigin(),
}: {
  env?: PublicAuthEnv;
  origin?: string | null;
} = {}): boolean => {
  const configured = parseBooleanEnv(env.VITE_AUTH_GOOGLE_ENABLED);
  if (configured !== null) {
    return configured;
  }
  return (
    isNonProductionAuthSurface({ env, origin }) &&
    hasGoogleOauthProvider({ origin, env }) &&
    isOriginSupportedForOauth(origin, { env })
  );
};

const resolveGoogleLoginHint = ({
  env = DEFAULT_PUBLIC_AUTH_ENV,
}: {
  env?: PublicAuthEnv;
} = {}): string | null => {
  const configured = env.VITE_AUTH_GOOGLE_LOGIN_HINT;
  if (typeof configured === "string") {
    const trimmed = configured.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return getGoogleLoginHintForCurrentOrigin();
};

export const resolveLocalPersonaSigninEnabled = ({
  env = DEFAULT_PUBLIC_AUTH_ENV,
  origin = getCurrentOrigin(),
}: {
  env?: PublicAuthEnv;
  origin?: string | null;
} = {}): boolean => {
  const configured = parseBooleanEnv(env.VITE_AUTH_LOCAL_PERSONA_SIGNIN_ENABLED);
  if (isProductionAuthSurface(origin)) {
    return false;
  }
  if (isLocalPersonaAuthSurface({ env, origin })) {
    return configured ?? true;
  }
  if (!isNonProductionAuthSurface({ env, origin })) {
    return false;
  }
  return true;
};

export const resolveLocalPersonaSkipPasswordEnabled = ({
  env = DEFAULT_PUBLIC_AUTH_ENV,
  origin = getCurrentOrigin(),
}: {
  env?: PublicAuthEnv;
  origin?: string | null;
} = {}): boolean => {
  const configured = parseBooleanEnv(env.VITE_AUTH_LOCAL_PERSONA_SKIP_PASSWORD);
  if (isProductionAuthSurface(origin)) {
    return false;
  }
  if (isLocalPersonaAuthSurface({ env, origin })) {
    return configured ?? true;
  }
  if (!isNonProductionAuthSurface({ env, origin })) {
    return false;
  }
  return false;
};

export const PUBLIC_AUTH_CONFIG = {
  googleSocialEnabled: resolveGoogleSocialEnabled(),
  googleLoginHint: resolveGoogleLoginHint(),
  localPersonaSigninEnabled: resolveLocalPersonaSigninEnabled(),
  localPersonaSkipPasswordEnabled: resolveLocalPersonaSkipPasswordEnabled(),
  localPersonaPassword: null as string | null,
} as const;
